import React from 'react';
import {
  GlobalSettingItemFragment,
  MediaImageFragment,
  PageCommunityDetail,
  PageCommunityDetailFragment
} from '@src/graphql/gql-types';
import Layout from '@src/components/Layout';
import SEO from '@src/components/SEO';
import HeroLarge from '@src/components/HeroLarge';
import HeroSmall from '@src/components/HeroSmall';
import DiscoverBlock from '@src/components/DiscoverBlock';
import { getPageUrlPath } from '@src/utils/url';
import { ContentfulPageHyperlinkFragments } from '@src/types/contentful';
import { MapLocation } from '@src/types/map';
import {
  generateMetaTitle,
  getFooterNavItems,
  getOgImage
} from '@src/utils/helpers';
import { getEnquiryFormValues } from '@src/utils/form';
import {
  getCommunitiesMegaMenuItemEntry,
  getFormEnquiryDisclaimerEntry,
  getGlobalContactEmailEntry,
  getGlobalContactPhoneNumberEntry,
  getGlobalCTAPanelEntry,
  getNationalContactUsHeadOfficeEntry,
  getSiteWideAlertBannerEntry
} from '@src/utils/globalSettings';
import { CONTENTFUL_PAGE_CONTENT_TYPES } from '@src/constants/contentful';
import { NationalStandardPageProps } from '@src/types/page';
import { createReplaceDictionary } from '@src/utils/strings';
import SearchResultsContent from '@src/components/SearchResults';
import NotFound from '@src/components/NotFound';
import HtmlComment from '@src/components/HtmlComment';
import IntroText from '@src/components/IntroText';
import GenericText from '@src/components/GenericText';
import { getLatestDate } from '@src/utils/date';
import { SITE_NAME } from '@src/constants/site';
import HeroCarousel from '@src/components/HeroCarousel';
import dynamic from 'next/dynamic';
import { transformCommunitiesByStateToHyperLinks } from '@src/utils/communityLocations';

const ContentBlocks = dynamic(() => import('@src/components/ContentBlocks'), {
  ssr: true
});

type PageNationalStandardTemplate = NationalStandardPageProps & {
  searchString?: string;
  notFound?: boolean;
};

const PageNationalStandardTemplate: React.FC<PageNationalStandardTemplate> = ({
  globalSettingsPayload,
  nationalBasePagePayload,
  pagePayload,
  pageContentBlocks,
  searchString,
  notFound,
  mapData
}) => {
  const globalSettingItems = globalSettingsPayload?.globalSettings
    ?.items as GlobalSettingItemFragment[];
  const pageData = pagePayload?.page?.items?.[0];
  const pagePathData = pagePayload?.pagePath
    ?.items?.[0] as ContentfulPageHyperlinkFragments;
  const { communities, footerNav, legalLinks, primaryNav, socialLinks } =
    nationalBasePagePayload || {};

  const communityItems = communities?.items as PageCommunityDetailFragment[];
  const title = pageData?.title;
  const slug = pageData?.slug;
  const seoTitle = pageData?.seoTitle;
  const seoDescription = pageData?.seoDescription;
  const noFollow = pageData?.noFollow;
  const noIndex = pageData?.noIndex;
  const hero = pageData?.hero;
  const introText = pageData?.introText;
  const discoverBlock = pageData?.discoverBlock;
  const disclaimer = pageData?.disclaimer;
  const publishedAt = pageData?.sys?.publishedAt;

  const metaTitle = seoTitle ?? generateMetaTitle([title, SITE_NAME]);
  const isHomePage = slug === '/';
  const heroCarousel = hero && hero?.__typename === 'HeroCarousel';
  const heroLarge = hero && hero?.__typename === 'HeroLarge';
  const heroSmall = hero && hero?.__typename === 'HeroSmall';
  const pageURLPath = getPageUrlPath(pagePathData);
  let canonicalUrl = pageURLPath;

  const communitiesMegaMenuItem =
    getCommunitiesMegaMenuItemEntry(globalSettingItems);
  const communityNav = transformCommunitiesByStateToHyperLinks(
    communities?.items as PageCommunityDetail[],
    communitiesMegaMenuItem
  );

  const globalCTAPanel = getGlobalCTAPanelEntry(globalSettingItems);
  const alertBanner = getSiteWideAlertBannerEntry(globalSettingItems);
  const formNationalContactUsHeadOfficeContent =
    getNationalContactUsHeadOfficeEntry(globalSettingItems);
  const formDisclaimerRichText =
    getFormEnquiryDisclaimerEntry(globalSettingItems);

  const replacementDictionary = createReplaceDictionary(
    globalSettingItems,
    getLatestDate(hero?.sys?.publishedAt, publishedAt)
  );

  const dynamicFormValues = getEnquiryFormValues(communityItems);
  let metaOgImage;
  if (heroLarge || heroSmall) {
    metaOgImage = getOgImage(hero?.mediaItem as MediaImageFragment);
  } else if (heroCarousel) {
    metaOgImage = getOgImage(
      hero?.slidesCollection?.items[0]?.image as MediaImageFragment
    );
  }

  const footerNavItems = getFooterNavItems(
    primaryNav,
    footerNav,
    socialLinks,
    legalLinks
  );

  if (mapData?.location) {
    for (const key in mapData.location) {
      if (mapData.location[key as keyof MapLocation] !== 'all') {
        canonicalUrl += `/${mapData.location[key as keyof MapLocation]}`;
      }
    }
    canonicalUrl = `${canonicalUrl}/`;
  }

  return (
    <Layout
      formType={'national_enquiry'}
      alertBannerData={alertBanner}
      primaryNav={primaryNav?.items[0]?.navigationElementsCollection?.items}
      communityNav={communityNav}
      communitiesMegaMenuItem={communitiesMegaMenuItem}
      footerNav={footerNavItems}
      dynamicFormValues={dynamicFormValues}
      formDisclaimerRichText={formDisclaimerRichText}
      headOfficePhoneNumber={
        getGlobalContactPhoneNumberEntry(globalSettingItems)?.value
      }
      headOfficeEmail={getGlobalContactEmailEntry(globalSettingItems)?.value}
    >
      <SEO
        metaTitle={metaTitle}
        metaDesc={seoDescription || ''}
        metaPageUrl={pageURLPath}
        canonicalUrl={canonicalUrl}
        metaOgImage={metaOgImage?.url}
        metaOgImageAlt={metaOgImage?.alt}
        noIndex={noIndex}
        noFollow={noFollow}
        globalSettingItems={globalSettingItems}
      />

      <HtmlComment comment="PageNationalStandard Template" />

      {/* components that oveflow the grid */}
      {heroCarousel && <HeroCarousel {...hero} />}
      {heroLarge && (
        <HeroLarge
          {...hero}
          isHomepage={isHomePage}
          slate={isHomePage}
          replacementDictionary={replacementDictionary}
        />
      )}
      {heroSmall && (
        <HeroSmall
          {...hero}
          replacementDictionary={replacementDictionary}
          isNewsEventsPage={pageData.slug?.includes('news')}
        />
      )}

      {/* Possibly not needed for homepage */}
      {introText && (
        <IntroText
          introText={introText}
          replacementDictionary={replacementDictionary}
        />
      )}

      {pageContentBlocks && (
        <ContentBlocks
          items={pageContentBlocks}
          pageType={CONTENTFUL_PAGE_CONTENT_TYPES.NATIONAL_STANDARD}
          staticBlockData={{
            nationalContactForm: {
              disclaimerRichText: formDisclaimerRichText,
              headOfficeContent: formNationalContactUsHeadOfficeContent
            },
            globalCTAPanel,
            communities: communityItems || undefined,
            primaryNav,
            mapData: mapData || undefined
          }}
          sharedBuildData={{ replacementDictionary }}
        />
      )}

      {discoverBlock && <DiscoverBlock {...discoverBlock} />}

      {disclaimer?.bodyContent && (
        <GenericText
          disclaimer
          {...disclaimer}
          pageType={CONTENTFUL_PAGE_CONTENT_TYPES.NATIONAL_STANDARD} // Forcing this type at the moment, but it might not be needed/or applied in this instance
          sharedBuildData={{ replacementDictionary }}
        />
      )}

      {/* render search results */}
      {searchString && <SearchResultsContent searchString={searchString} />}

      {/* render 404 not found */}
      {notFound && <NotFound />}
    </Layout>
  );
};

export default PageNationalStandardTemplate;
